import { useEffect, useState, useCallback } from 'react';
import { DescriptionBar } from 'components';
import { PromotionCard } from './PromotionCard';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useInfiniteScroll } from './hooks/useInfiniteScroll';

// API 응답 타입 정의
interface PromotionApiResponse {
  promotionId: number;
  imagePath: string;
  title: string;
  description: string;
  notice: string;
  buttonText: string;
  buttonLink: string;
  promotionStartDate: string;
  promotionEndDate: string;
  createdDate: string;
}

interface PromotionSearchResponse {
  content: PromotionApiResponse[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

const Promotion = () => {
  const { t } = useTranslation();
  const [promotions, setPromotions] = useState<PromotionApiResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalElements, setTotalElements] = useState(0);

  // 프로모션 데이터 조회 (초기 로드)
  const fetchPromotions = async (isInitial = true) => {
    try {
      if (isInitial) {
        setLoading(true);
        setError(null);
        setPage(0);
        setHasMore(true);
      }

      const searchParams = {
        page: isInitial ? 0 : page,
        size: 3, // 페이지당 3개씩 로드
        sortBy: 'createdDate',
        sortDirection: 'DESC'
      };

      const response = await axios.get('/api/promotions/search', { params: searchParams });
      
      if (response.data.code === 'SUCCESS') {
        const responseData: PromotionSearchResponse = response.data.data;
        
        if (isInitial) {
          setPromotions(responseData.content);
        } else {
          setPromotions(prev => [...prev, ...responseData.content]);
        }
        
        setTotalElements(responseData.totalElements);
        setHasMore(responseData.content.length > 0 && (page + 1) < responseData.totalPages);
        setPage(prev => prev + 1);
      } else {
        setError('프로모션 조회에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('프로모션 조회 실패:', error);
      if (error.response?.status === 500) {
        setError('서버 내부 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
      } else {
        setError('프로모션 조회에 실패했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  // 추가 데이터 로드
  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return;
    await fetchPromotions(false);
  }, [hasMore, loading, page]);

  // 무한 스크롤 훅 사용
  const { observerRef } = useInfiniteScroll({
    hasMore,
    loading,
    onLoadMore: loadMore,
    threshold: 200
  });

  useEffect(() => {
    fetchPromotions();
  }, []);

  // 이미지 URL 생성 함수
  const getPromotionImageUrl = (imagePath: string) => {
    if (!imagePath) return '';
    
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    
    // 개발 환경과 프로덕션 환경 모두 지원
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 
      (process.env.NODE_ENV === 'production' ? 'https://iopsspace.com' : 'http://localhost:8080');
    
    return `${baseUrl}${imagePath}`;
  };

  return (
    <>
      <Helmet>
        <title>{t('promotion.pageTitle')} | IOPS SPACE</title>
        <meta name="description" content={t('promotion.pageDescription')} />
      </Helmet>
      <div className="h-full w-full max-w-[1200px] mx-auto">
        <DescriptionBar
          className="mb-2 md:pt-1"
          title={t('promotion.pageTitle')}
          description={t('promotion.pageDescription')}
        />
        


        {/* 에러 메시지 */}
        {error && (
          <div className="px-4 md:px-6 mb-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* 로딩 상태 */}
        {loading && (
          <div className="px-4 md:px-6 mb-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-blue-800">프로모션을 불러오는 중...</p>
            </div>
          </div>
        )}

        {/* 프로모션 카드 리스트 */}
        <div className="flex w-full flex-1 flex-col gap-4 px-4 py-6 md:px-0">
          {promotions.length === 0 && !loading && !error ? (
            <div className="text-center py-8 text-gray-500">
              표시할 프로모션이 없습니다.
            </div>
          ) : (
            <>
              {promotions.map((promotion) => (
                <PromotionCard
                  key={promotion.promotionId}
                  title={promotion.title}
                  description={promotion.description}
                  notice={promotion.notice}
                  imageUrl={getPromotionImageUrl(promotion.imagePath)}
                  buttonText={promotion.buttonText}
                  buttonLink={promotion.buttonLink}
                  onJoinClick={() => {
                    if (promotion.buttonLink) {
                      if (promotion.buttonLink.startsWith('http')) {
                        window.open(promotion.buttonLink, '_blank');
                      } else {
                        window.location.href = promotion.buttonLink;
                      }
                    }
                  }}
                  aria-label={`${promotion.title} 프로모션 참여하기`}
                />
              ))}
              
              {/* 무한 스크롤 감지 요소 */}
              <div ref={observerRef} className="h-4" />
              
              {/* 추가 로딩 상태 */}
              {hasMore && (
                <div className="text-center py-4">
                  <div className="inline-flex items-center gap-2 text-blue-600">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span className="text-sm">Loading more...</span>
                  </div>
                </div>
              )}
              
              {/* 전체 데이터 표시 완료 */}
              {!hasMore && promotions.length > 0 && (
                <div className="text-center py-4">
                  <span className="text-sm text-gray-500">
                    All promotions have been loaded.(Total:{totalElements})
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Promotion;
